import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { useMediaQuery } from 'react-responsive';

import Button from '../../../../../../../components/global/Button';
import useBasename from '../../../../../../../components/routing/useBasename';
import { isAlbum } from '../../../../../../../util/album';
import {
  isAlbumFolderOwner,
  isAlbumOwner,
} from '../../../../../../../util/account';
import { MeQuery } from '../../../../../../../graphql/queries/account';
import useViewPermission from '../../../../../services/contributors/hooks/useViewPermission';
import useUploadPermission from '../../../../../services/contributors/hooks/useUploadPermission';
import useVideoUploadPermission from '../../../../../services/contributors/hooks/useVideoUploadPermission';
import { canContributeToEntity } from '../../../../../../../components/global/ContributeButton/helpers';
import { ALBUM_TYPE } from '../../../../../../../constants/albums';
import {
  shouldRenderFavorites,
  shouldRenderMatches,
  shouldRenderPhotos,
  shouldRenderRouteSelector,
  shouldRenderVideos,
  shouldRenderBlogs,
} from '../helpers';
import useFeaturePermission, {
  FeaturePermissionBlock,
} from '../../../../../services/featurePermissions';
import { getAsset } from '../../../../../../../util/assets';
import {
  PURCHASED_EVENT_FEATURES,
  PURCHASED_EVENT_ADDON_FEATURES,
  SEARCH_SUBSCRIPTION_FEATURES,
} from '../../../../../services/featurePermissions/helpers';
import { HierarchyItemForFavorites } from '../../../../../../../graphql/queries/hierarchy-item';
import HierarchyItemDropdown from '../../HierarchyItemDropdown';
import { AlbumFolderShutterbugFilterContextProvider } from '../../../../AlbumFolder/AlbumFolders/AlbumFoldersShutterbugHome/hooks/useAlbumFolderShutterbugFilterContext';
import { breakpointDown } from '../../../../../../../services/responsive/responsive';
import { getAlbumMembershipGroupNavigationQuery } from '../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroupNavigation';

const propTypes = {
  albumMembershipGroup: PropTypes.shape({
    album_memberships_has_all_photos_access: PropTypes.bool,
  }),
  albumPhotoGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMemberships: PropTypes.shape({
    records: PropTypes.arrayOf(
      PropTypes.shape({
        album: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          custom_prints_meta: PropTypes.shape({
            onsite_delivery_enabled: PropTypes.bool,
          }),
        }),
      }),
    ),
  }),
  hierarchyItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  hierarchyItemForFavorites: PropTypes.shape({
    album: PropTypes.shape({
      id: PropTypes.string,
    }),
    album_folder: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  isConsumerView: PropTypes.bool,
  className: PropTypes.string,
  blogPostsTotal: PropTypes.number,
  dropdownEnabled: PropTypes.bool,
};

const MATCHES_PATHS = {
  ALL: 'ALL',
  MATCHES: 'MATCHES',
  FAVORITES: 'FAVORITES',
  VIDEOS: 'VIDEOS',
  BLOG: 'BLOG',
};

const RouteSelector = ({
  albumMembershipGroup,
  albumPhotoGroup,
  albumMemberships,
  hierarchyItem,
  hierarchyItemForFavorites,
  location,
  user,
  isConsumerView,
  className,
  blogPostsTotal,
  dropdownEnabled = false,
}) => {
  const basename = useBasename();
  // View permissions are the basic definer of whether or not you can access the /album/
  // route or not since, at the end of the day, you could have upload permissions and
  // be a contributor,but that wouldn't mean you can go ahead and view everything.
  const { isAllowed: isAllowedToView, isContributor } = useViewPermission({
    entityId: hierarchyItem?.id,
  });

  const itemIsAlbum = useMemo(() => isAlbum(hierarchyItem), [hierarchyItem]);
  const isMobile = useMediaQuery(breakpointDown('sm'));

  const albumEntity =
    albumMembershipGroup?.album ?? (itemIsAlbum ? hierarchyItem : null);
  const albumFolderEntity =
    albumMembershipGroup?.album_folder ?? (!itemIsAlbum ? hierarchyItem : null);

  const isOwner = useMemo(
    () =>
      itemIsAlbum
        ? isAlbumOwner(user, hierarchyItem)
        : isAlbumFolderOwner(user, hierarchyItem),
    [user, hierarchyItem, itemIsAlbum],
  );

  // Mirror the determination made on HierarchyItemDropdown for the "Upload
  // Photos" ability (ContributeButton). The dropdown only renders alongside the
  // route selector, so we must surface the same upload-ability here to decide
  // whether the selector should render for an otherwise empty album.
  const uploadEntity = albumEntity ?? albumFolderEntity;

  const { isAllowed: isAllowedToUpload } = useUploadPermission({
    entityId: uploadEntity?.id,
  });

  const { isAllowed: isAllowedToUploadVideo } = useVideoUploadPermission({
    entityId: uploadEntity?.id,
    // Important to check for ownership here. Otherwise, we'll assume the user is
    // the owner when it isn't a contributor
    checkOwnership: true,
  });

  const canUpload = useMemo(() => {
    const entityIsEvent = uploadEntity?.album_type === ALBUM_TYPE.EVENT;
    const externalUpload = uploadEntity?.enable_external_upload;
    const uploadAllowed =
      !!isAllowedToUpload || (entityIsEvent && externalUpload);
    const videoUploadAllowed =
      !!isAllowedToUploadVideo || (entityIsEvent && externalUpload);

    return canContributeToEntity(
      uploadEntity,
      uploadAllowed,
      videoUploadAllowed,
    );
  }, [uploadEntity, isAllowedToUpload, isAllowedToUploadVideo]);

  const featurePermissionsItem = useFeaturePermission({
    features: [PURCHASED_EVENT_FEATURES.joins_enabled],
    albumFolderId: isOwner ? hierarchyItem?.id : undefined,
    ownerOnly: true,
  });

  const rootAlbumFolderId =
    hierarchyItem?.root_album_folder?.id ||
    // Covers the album folder case
    hierarchyItem?.root?.id ||
    // Fallback
    hierarchyItem?.id;

  const allPhotosPath = useMemo(() => {
    if (!isOwner && !isAllowedToView) {
      return isConsumerView
        ? `/consumer/matches/${
            albumMembershipGroup?.id || hierarchyItem?.id
          }/all`
        : `/app/${basename}/hierarchy-items/${
            albumMembershipGroup?.id || hierarchyItem?.id
          }`;
    }

    if (itemIsAlbum) {
      return `/app/${basename}/albums/${hierarchyItem?.id}`;
    }

    return `/app/${basename}/folders/${hierarchyItem?.id}`;
  }, [
    isOwner,
    isAllowedToView,
    itemIsAlbum,
    basename,
    albumMembershipGroup,
    hierarchyItem,
  ]);

  const matchesPath = useMemo(() => {
    if (
      location?.pathname ===
        `/app/${basename}/hierarchy-items/${hierarchyItem?.id}/matches` ||
      location?.pathname === `/consumer/matches/${hierarchyItem?.id}`
    ) {
      return MATCHES_PATHS.MATCHES;
    }

    if (
      location?.pathname ===
      `/app/${basename}/hierarchy-items/${
        albumMembershipGroup?.id || hierarchyItem?.id
      }/favorites`
    ) {
      return MATCHES_PATHS.FAVORITES;
    }

    if (
      location?.pathname ===
      `/app/${basename}/hierarchy-items/${
        albumMembershipGroup?.id || hierarchyItem?.id
      }/videos`
    ) {
      return MATCHES_PATHS.VIDEOS;
    }

    if (
      location?.pathname ===
      `/app/${basename}/hierarchy-items/${hierarchyItem?.id}/blogs`
    ) {
      return MATCHES_PATHS.BLOGS;
    }

    return MATCHES_PATHS.ALL;
  }, [location, basename, hierarchyItem, albumMembershipGroup]);

  const shouldRenderSelector = useMemo(
    () =>
      shouldRenderRouteSelector(
        user,
        hierarchyItem,
        albumMembershipGroup,
        isAllowedToView,
        isConsumerView,
        canUpload,
      ),
    [user, hierarchyItem, albumMembershipGroup, isAllowedToView, canUpload],
  );

  const shouldRenderPhotosHeader = useMemo(
    () =>
      shouldRenderPhotos(
        user,
        hierarchyItem,
        albumMembershipGroup,
        isAllowedToView,
      ),
    [user, hierarchyItem, albumMembershipGroup, isAllowedToView],
  );

  const shouldRenderMatchesHeader = useMemo(
    () =>
      shouldRenderMatches({
        hierarchyItem,
        albumMembershipGroup,
        isContributor,
        featurePermissionsItem,
      }),
    [
      hierarchyItem,
      albumMembershipGroup,
      isContributor,
      featurePermissionsItem,
    ],
  );

  const shouldRenderFavoritesHeader = useMemo(
    () =>
      !isConsumerView &&
      shouldRenderFavorites(
        hierarchyItemForFavorites?.album ??
          hierarchyItemForFavorites?.album_folder,
      ),
    [hierarchyItemForFavorites],
  );

  const shouldRenderVideosHeader = useMemo(() => {
    return (
      !isConsumerView &&
      shouldRenderVideos({ hierarchyItem, albumMembershipGroup })
    );
  }, [hierarchyItem, albumMembershipGroup]);

  const shouldRenderBlogsHeader = useMemo(() => {
    return (
      !isConsumerView &&
      shouldRenderBlogs({ hierarchyItem, albumMembershipGroup, blogPostsTotal })
    );
  }, [hierarchyItem, albumMembershipGroup, blogPostsTotal]);

  const hasSubFolders = useMemo(() => {
    const albumFolders =
      hierarchyItem?.album_folder?.children?.album_folders?.records ||
      hierarchyItem?.children?.album_folders?.records ||
      [];

    return albumFolders?.length > 0;
  }, [hierarchyItem, itemIsAlbum]);

  const hasAlbumsOrSubAlbums = useMemo(() => {
    const albums =
      hierarchyItem?.album_folder?.children?.albums?.records ||
      hierarchyItem?.children?.albums?.records ||
      [];
    const albumPhotoGroupsTotal = itemIsAlbum
      ? (hierarchyItem?.album_photo_groups?.total ?? 0)
      : 0;

    return albums.length > 0 || albumPhotoGroupsTotal > 0;
  }, [hierarchyItem, itemIsAlbum]);

  const photosOrAlbumsLabel = useMemo(() => {
    if (albumPhotoGroup) {
      return 'Photos';
    }

    if (hasSubFolders) {
      return 'Folders';
    }

    if (hasAlbumsOrSubAlbums) {
      return 'Albums';
    }

    return 'Photos';
  }, [albumPhotoGroup, hasSubFolders, hasAlbumsOrSubAlbums]);

  const classes = classNames({
    'matches-set-selector-container': true,
    [className]: !!className,
  });

  // We use and need the divider from below on PM view, but it's not needed on Consumer View.
  if (isConsumerView && !shouldRenderSelector) {
    return null;
  }

  return (
    <div className={classes}>
      {shouldRenderSelector && (
        <Fragment>
          {shouldRenderMatchesHeader && (
            <Button
              btnStyle="reset"
              className={classNames({
                'matches-set-selector': true,
                'is-active': matchesPath === MATCHES_PATHS.MATCHES,
              })}
              to={
                isConsumerView
                  ? // For consumer, always force the matches path to push to root.
                    `/consumer/matches/${
                      rootAlbumFolderId ?? hierarchyItem?.id
                    }`
                  : `/app/${basename}/hierarchy-items/${hierarchyItem?.id}/matches`
              }
            >
              {isOwner && (
                <FeaturePermissionBlock
                  purchasedEventFeatures={[
                    PURCHASED_EVENT_FEATURES.free_joins_enabled,
                  ]}
                  searchSubscriptionFeatures={[
                    SEARCH_SUBSCRIPTION_FEATURES.owned_content_free_joins_enabled,
                  ]}
                  purchasedEventAddonFeatures={[
                    PURCHASED_EVENT_ADDON_FEATURES.owned_content_free_joins_enabled,
                  ]}
                  albumFolderId={
                    // Covers the album case
                    hierarchyItem?.root_album_folder?.id ||
                    // Covers the album folder case
                    hierarchyItem?.root?.id ||
                    // Fallback
                    hierarchyItem?.id
                  }
                  fallbackContent={() => (
                    <>
                      People{' '}
                      <img
                        src={getAsset('/diamond-orange.svg')}
                        className="matches-freemium-icon"
                        alt="Unlock your potential"
                      />
                    </>
                  )}
                >
                  People
                </FeaturePermissionBlock>
              )}
              {!isOwner && <Fragment>People</Fragment>}
            </Button>
          )}
          {shouldRenderPhotosHeader && (
            <Button
              btnStyle="reset"
              className={classNames({
                'matches-set-selector': true,
                'is-active': matchesPath === MATCHES_PATHS.ALL,
              })}
              to={allPhotosPath}
            >
              {photosOrAlbumsLabel}
            </Button>
          )}
          {shouldRenderFavoritesHeader && (
            <Button
              btnStyle="reset"
              className={classNames({
                'matches-set-selector': true,
                'is-active': matchesPath === MATCHES_PATHS.FAVORITES,
              })}
              to={`/app/${basename}/hierarchy-items/${hierarchyItem?.id}/favorites`}
            >
              {isMobile ? 'Faves' : 'Favorites'}
            </Button>
          )}
          {shouldRenderVideosHeader && (
            <Button
              btnStyle="reset"
              className={classNames({
                'matches-set-selector': true,
                'is-active': matchesPath === MATCHES_PATHS.VIDEOS,
              })}
              to={`/app/${basename}/hierarchy-items/${hierarchyItem?.id}/videos`}
            >
              Videos
            </Button>
          )}
          {shouldRenderBlogsHeader && (
            <Button
              btnStyle="reset"
              className={classNames({
                'matches-set-selector': true,
                'is-active': matchesPath === MATCHES_PATHS.BLOGS,
              })}
              to={`/app/${basename}/hierarchy-items/${hierarchyItem.id}/blogs`}
            >
              Blog
            </Button>
          )}
          {dropdownEnabled && !isMobile && (
            <AlbumFolderShutterbugFilterContextProvider>
              <HierarchyItemDropdown
                album={albumEntity}
                albumFolder={albumFolderEntity}
                albumMembershipGroup={albumMembershipGroup}
                albumMemberships={albumMemberships}
                isAlbum={itemIsAlbum}
                userId={user?.id}
                hierarchyItem={hierarchyItem}
              />
            </AlbumFolderShutterbugFilterContextProvider>
          )}
        </Fragment>
      )}
    </div>
  );
};

RouteSelector.propTypes = propTypes;

const withUser = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    user: me,
  }),
});

const withHierarchyItemForFavorites = graphql(HierarchyItemForFavorites, {
  options: ({ match }) => ({
    variables: {
      id:
        match?.params?.albumId ??
        match?.params?.folderId ??
        match?.params?.hierarchyItemId,
    },
    fetchPolicy: 'network-only',
  }),
  props: ({ data: { hierarchyItem } }) => ({
    // Contributors use case.
    hierarchyItemForFavorites: hierarchyItem,
  }),
  skip: ({ match }) =>
    !match?.params?.albumId &&
    !match?.params?.folderId &&
    !match?.params?.hierarchyItemId,
});

const withAlbumMembershipNavigation = graphql(
  getAlbumMembershipGroupNavigationQuery,
  {
    options: ({ albumMembershipGroup }) => ({
      variables: {
        id: albumMembershipGroup?.id,
      },
      fetchPolicy: 'cache-first',
    }),
    props: ({ data: { albumMembershipGroup } }) => ({
      albumMemberships: albumMembershipGroup?.album_memberships_collection,
    }),
    skip: ({ albumMembershipGroup }) => !albumMembershipGroup?.id,
  },
);

export default compose(
  withRouter,
  withUser,
  withHierarchyItemForFavorites,
  withAlbumMembershipNavigation,
)(RouteSelector);
